import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import "./dashboard.css";
import Instance from "../../AxiosConfig";
import { Spin, Segmented } from "antd";

const TotalCompaniesGraph = () => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;

  const [chartData, setChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("");

  const [manualMax, setManualMax] = useState(""); // NEW FIELD
  const [viewMode, setViewMode] = useState("closure");
// closure | target | achievement

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Auto-select current year
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setSelectedYear(currentYear.toString());
  }, []);

   
  useLayoutEffect(() => {
    if (!chartRef.current) return;
    const ctx = chartRef.current.getContext("2d");

    // ensure values is always an array
    const values = monthNames.map(
  (month) => Number(chartData?.[month]?.[viewMode] || 0)
);

    // find max from data
    const maxValue = Math.max(0, ...values);

    // DEFAULT Y-AXIS = 10 LAKHS
    const DEFAULT_Y_MAX =
  viewMode === "achievement"
    ? 200
    : 1000000;

    let yMax;

    if (manualMax) {
      // user manually entered max value
      yMax = Number(manualMax);
    } else {
      // auto mode → choose highest between dataMax * 1.1 and default 10L
      const calculatedMax =
  viewMode === "achievement"
    ? Math.ceil(Math.max(100, maxValue))
    : Math.ceil((maxValue || 100000) * 1.1);
      yMax = Math.max(DEFAULT_Y_MAX, calculatedMax);
    }

    // always 5 divisions
    const stepSize = Math.ceil(yMax / 5);

    const lineData = {
      labels: monthNames,
      datasets: [
        {
          label:
  viewMode === "closure"
    ? "Closure Amount"
    : "Achievement %",
          data: values,
          backgroundColor: "rgba(19, 106, 213, 0.7)",
          borderColor: "#136AD5",
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
          barThickness: 28,
          maxBarThickness: 28,
          categoryPercentage: 0.7,
          barThickness: window.innerWidth < 768 ? 20 : 28,
          maxBarThickness: window.innerWidth < 768 ? 20 : 28,
          maxRotation: window.innerWidth < 768 ? 90 : 25,
          minRotation: window.innerWidth < 768 ? 90 : 25,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,

      scales: {
        y: {
          beginAtZero: true,
          max: yMax,
          ticks: {
            stepSize: stepSize,
            callback: (value) =>   viewMode === "achievement"     ? `${value}%`     : Number(value).toLocaleString("en-IN"),
            color: "#9ca3af",
            font: { size: 14 },
          },
          grid: {
            color: "rgba(0,0,0,0.1)",
            drawBorder: false,
            drawTicks: false,
          },
        },
        x: {
          ticks: {
            color: "#64748B",
            font: {
              size: window.innerWidth < 768 ? 10 : 12,
            },
            maxRotation: window.innerWidth < 768 ? 25 : 45,
            minRotation: window.innerWidth < 768 ? 25 : 0,
            autoSkip: false,
          },
          grid: { display: false },
        },
      },
      plugins: {
  legend: {
    display: false,
  },
  tooltip: {
    callbacks: {
      label: (context) => {
        const month = context.label;
        const monthData = chartData?.[month];

        if (viewMode === "closure") {
          return `Closure : ₹${Number(
            monthData?.closure || 0
          ).toLocaleString("en-IN")}`;
        }

        return [
          `Achievement : ${monthData?.achievement || 0}%`,
          `Closure : ₹${Number(
            monthData?.closure || 0
          ).toLocaleString("en-IN")}`,
          `Target : ₹${Number(
            monthData?.target || 0
          ).toLocaleString("en-IN")}`,
        ];
      },
    },
  },
},  
    };

    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    chartInstanceRef.current = new Chart(ctx, {  
      type: "bar",
      data: lineData,
      options,
    });
  }, [chartData, manualMax, viewMode]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const response = await Instance.get("/agents?noPagination=true", {
        params: { page, limit: itemsPerPage },
      });

      if (response.status === 200 || response.status === 201) {
        setData(response?.data?.agents || []);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    if (!selectedYear || !selectedAgent) return;

    setLoading(true);
    try {
      const response = await Instance.get(
        `/dashboard/agent-monthwise-closure`,
        {
          params: {
            year: selectedYear,
            agentId: selectedAgent,
          },
        }
      );
      console.log("Chart Data Response:", response);
      if (response.status === 200 || response.status === 201) {
        const monthWise =
          response.data.monthWise || response.data.monthwise || {};

        setChartData(monthWise);
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
      const empty = {};

monthNames.forEach((m) => {
  empty[m] = {
    closure: 0,
    target: 0,
    achievement: 0,
  };
});

setChartData(empty);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [selectedYear, selectedAgent]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (data.length > 0 && !selectedAgent) {
      setSelectedAgent(data[0]._id);
    }
  }, [data]);

  return (
    <div className="chart-card mt-4">
      <div className="chart-header">
        <h2 className="chart-title">
  Monthly Agent Performance
  <span
    style={{
      marginLeft: 10,
      fontSize: 14,
      color: "#64748B",
      fontWeight: 400,
    }}
  >
    (
    {viewMode === "closure"
      ? "Closure"
      : viewMode === "target"
      ? "Target"
      : "Achievement %"}
    )
  </span>
</h2>

        <div className="chart-dropdowns">
          {/* Year Dropdown */}
          <select
            className="chart-dropdown"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="" disabled>
              Select Year
            </option>
            {Array.from({ length: 2050 - 2025 + 1 }, (_, i) => 2025 + i).map(
              (year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              )
            )}
          </select>

          <select
            className="chart-dropdown"
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
          >
            <option value="" disabled>
              Select Agent
            </option>
            {data.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.agentName}
              </option>
            ))}
          </select>
          <Segmented
  value={viewMode}
  onChange={setViewMode}
  options={[
    {
      label: "Closure",
      value: "closure",
    },
    {
      label: "Achievement %",
      value: "achievement",
    },
  ]}
/>

        <input
  type="number"
  placeholder={
    viewMode === "achievement"
      ? "Max Percentage"
      : "Enter Max Amount"
  }
  className="chart-dropdown"
  value={manualMax}
  onChange={(e) => setManualMax(e.target.value)}
/>
        </div>
      </div>

      <div
        className="chart-body"
        style={{ position: "relative", minHeight: 300 }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "8px",
            }}
          >
            <Spin size="large" />
          </div>
        )}

        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

export default TotalCompaniesGraph;
